import { useMemo } from 'react';
import { useGetAtsDepartmentsQuery } from '@/store/query/departments';
import { useGetAtsOfficesQuery } from '@/store/query/offices';
import { useSelector } from 'react-redux';
import { selectUsers } from '@/store/users/selectors';
import {
  EMPLOYMENT_TYPE_OPTIONS,
  REQUISITION_TYPE_OPTIONS,
} from '@/constants/requisitions/enums';
import { JobListMenu } from '@/components/ats/filters/components/UiItems/JobList';
import { useGetAllJobsListQuery } from '@/store/query/ats-jobs';
import {
  DepartmentMultiValue,
  SelectOptionWithAvatar,
  SelectParentChildRootOption,
} from '@/components/formBuilder/SelectComponents';
import { requisitionStatusConfig } from '@/constants/requisitions';

/**
 * Hook to get filter configuration for requisitions
 * @param filterIds Optional array of filter IDs to include in the configuration
 * @returns Filter configuration and sort options
 */
export const useRequisitionFiltersConfig = (
  filterIds?: string[],
  isListPage?: boolean,
  associateOfferHireStatus?: boolean,
  associateJobStatus?: boolean
) => {
  const { data: departments } = useGetAtsDepartmentsQuery({});
  const { data: offices } = useGetAtsOfficesQuery({});
  const allUsers = useSelector(selectUsers);
  const usersOptions = allUsers?.map((user) => ({
    ...user,
    label: user?.name,
    value: user?.id,
  }));
  const { data: atsJobs = [] } = useGetAllJobsListQuery({
    params: {
      scope: 'viewable',
    },
  });

  const requisitionStatusOptions = useMemo(() => {
    const allStatusOptions = Object.entries(requisitionStatusConfig).map(
      ([key, value]) => ({
        ...value,
        key,
        value: value.id,
      })
    );

    // For offer and hire , only show approved, open, and created statuses
    if (associateOfferHireStatus) {
      const allowedStatuses = ['approved', 'open', 'created'];
      return allStatusOptions.filter((option) =>
        allowedStatuses.includes(option.id)
      );
    }

    if (associateJobStatus) {
      const allowedStatuses = [
        'draft',
        'pending_approval',
        'approved',
        'rejected',
        'created',
        'open',
      ];
      return allStatusOptions.filter((option) =>
        allowedStatuses.includes(option.id)
      );
    }

    return allStatusOptions;
  }, [associateOfferHireStatus, associateJobStatus]);

  // eslint-disable-next-line react-hooks/preserve-manual-memoization -- manual deps intentionally curated
  const requisitionFilters = useMemo(() => {
    return {
      status: {
        filterLabel: 'Status',
        name: 'status',
        labelField: 'label',
        isMulti: true,
        isCompare: false,
        isSearchable: true,
        isBoolean: false,
        label: '',
        isRequired: false,
        options: requisitionStatusOptions,
        validationType: 'array',
        type: 'select',
        isNewFilter: true,
        isAppendToBody: true,
      },
      employment_type: {
        filterLabel: 'Work type',
        name: 'employment_type',
        labelField: 'label',
        isMulti: true,
        isCompare: false,
        isSearchable: true,
        isBoolean: false,
        label: '',
        isRequired: false,
        options: EMPLOYMENT_TYPE_OPTIONS,
        validationType: 'array',
        type: 'select',
        isNewFilter: true,
        isAppendToBody: true,
      },
      department_ids: {
        filterLabel: 'Department',
        name: 'department_ids',
        isMulti: true,
        isCompare: false,
        isSearchable: true,
        isBoolean: false,
        isRequired: false,
        options: [
          !isListPage
            ? { name: 'All departments', id: 'all', parent_id: null }
            : {},
          ...(Array.isArray(departments) ? departments : []),
        ],
        valueKey: 'id',
        labelKey: 'name',
        components: {
          Menu: SelectParentChildRootOption,
          MultiValue: DepartmentMultiValue,
        },
        validationType: 'array',
        type: 'select',
        isNewFilter: true,
        isAppendToBody: true,
        placeholder: 'Search department',
      },
      office_ids: {
        filterLabel: 'Office',
        name: 'office_ids',
        labelField: 'name',
        isMulti: true,
        isCompare: false,
        isSearchable: true,
        isBoolean: false,
        label: '',
        isRequired: false,
        options:
          offices?.map((office) => ({
            value: office.id,
            label: office.name,
          })) || [],
        validationType: 'array',
        type: 'select',
        isNewFilter: true,
        isAppendToBody: true,
      },
      created_at: {
        filterLabel: 'Created at',
        name: 'created_at',
        isCompare: false,
        isMulti: false,
        isBoolean: true,
        label: '',
        isRequired: false,
        validationType: 'array',
        type: 'filterDateRangePicker',
        isCustomize: false,
        isNewFilter: true,
        fieldKey: 'date',
        isAppendToBody: true,
      },
      target_hire_date: {
        filterLabel: 'Target hire date',
        name: 'target_hire_date',
        isCompare: false,
        isMulti: false,
        isBoolean: true,
        label: '',
        isRequired: false,
        validationType: 'array',
        type: 'filterDateRangePicker',
        isCustomize: false,
        isNewFilter: true,
        fieldKey: 'date',
        isAppendToBody: true,
        showAllDates: true,
      },
      target_start_date: {
        filterLabel: 'Target start date',
        name: 'target_start_date',
        isCompare: false,
        isMulti: false,
        isBoolean: true,
        label: '',
        isRequired: false,
        validationType: 'array',
        type: 'filterDateRangePicker',
        isCustomize: false,
        isNewFilter: true,
        fieldKey: 'date',
        isAppendToBody: true,
        showAllDates: true,
      },

      created_by_ids: {
        filterLabel: 'Created by',
        name: 'created_by_ids',
        isMulti: true,
        isCompare: false,
        isSearchable: true,
        isBoolean: false,
        label: '',
        isRequired: false,
        validationType: 'array',
        type: 'select',
        isNewFilter: true,
        isAppendToBody: true,
        options: usersOptions || [],
        customFormatLabel: SelectOptionWithAvatar,
      },
      requisition_type: {
        filterLabel: 'Requisition type',
        name: 'requisition_type',
        labelField: 'label',
        isMulti: true,
        isCompare: false,
        isSearchable: true,
        isBoolean: false,
        label: '',
        isRequired: false,
        validationType: 'array',
        type: 'select',
        isNewFilter: true,
        isAppendToBody: true,
        options: REQUISITION_TYPE_OPTIONS,
      },
      recruiter_ids: {
        filterLabel: 'Recruitment partner',
        name: 'recruiter_ids',
        labelField: 'name',
        label: '',
        isMulti: true,
        isCompare: false,
        isSearchable: true,
        isBoolean: false,
        isRequired: false,
        validationType: 'array',
        type: 'select',
        isNewFilter: true,
        isAppendToBody: true,
        options: usersOptions,
        customFormatLabel: SelectOptionWithAvatar,
      },
      hiring_manager_ids: {
        filterLabel: 'Hiring manager',
        name: 'hiring_manager_ids',
        label: '',
        labelField: 'name',
        isMulti: true,
        isCompare: false,
        isSearchable: true,
        isBoolean: false,
        isRequired: false,
        validationType: 'array',
        type: 'select',
        isNewFilter: true,
        isAppendToBody: true,
        options: usersOptions,
        customFormatLabel: SelectOptionWithAvatar,
      },
      ats_job_ids: {
        filterLabel: 'Associated job',
        name: 'ats_job_ids',
        isMulti: true,
        isCompare: false,
        isSearchable: true,
        isBoolean: false,
        label: '',
        isRequired: false,
        options: (atsJobs || [])?.map((item) => ({
          value: item?.id,
          label: item?.name,
          status: item?.status || 'published',
          department: item?.department,
          locations: item?.locations || [],
        })),
        menuPlacement: 'auto',
        newStyles: [
          {
            type: 'menu',
            newStyles: {
              padding: '0px !important',
              borderRadius: '6px !important',
              border: '1px solid #EDEDED',
              borderColor: 'border-color',
              boxShadow: 'none',
            },
          },
        ],
        validationType: 'array',
        type: 'select',
        isNewFilter: true,
        isAppendToBody: true,
        components: {
          Menu: JobListMenu,
          IndicatorSeparator: () => null,
        },
        filterOption: () => true,
      },
    };
  }, [departments, offices, usersOptions, atsJobs, requisitionStatusOptions]); // eslint-disable-line react-hooks/exhaustive-deps

  const sortOptions = useMemo(
    () => [
      {
        id: 'created_at',
        label: 'Created date (Ascending)',
        element: 'created_at',
        order: 'asc',
      },
      {
        id: 'created_at',
        label: 'Created date (Descending)',
        element: 'created_at',
        order: 'desc',
      },
      {
        id: 'opened_at',
        label: 'Days open (Highest-first)',
        element: 'opened_at',
        order: 'desc',
      },
      {
        id: 'opened_at',
        label: 'Days open (Lowest-first)',
        element: 'opened_at',
        order: 'asc',
      },
      // {
      //   id: 'target_hire_date',
      //   label: 'Target hire date (Ascending)',
      //   element: 'target_hire_date',
      //   order: 'desc',
      // },
      // {
      //   id: 'target_hire_date',
      //   label: 'Target hire date (Descending)',
      //   element: 'target_hire_date',
      //   order: 'asc',
      // },
      // {
      //   id: 'target_start_date',
      //   label: 'Target start date (Ascending)',
      //   element: 'target_start_date',
      //   order: 'desc',
      // },
      // {
      //   id: 'target_start_date',
      //   label: 'Target start date (Descending)',
      //   element: 'target_start_date',
      //   order: 'asc',
      // },
    ],
    []
  );

  // Filter the configuration based on provided filterIds
  const filterConfig = useMemo(() => {
    // If no filterIds are provided, return all filters
    if (!filterIds || filterIds.length === 0) {
      return requisitionFilters;
    }

    return filterIds.reduce(
      (acc, filterId) => {
        if (requisitionFilters[filterId]) {
          acc[filterId] = requisitionFilters[filterId];
        }
        return acc;
      },
      {} as typeof requisitionFilters
    );
  }, [filterIds, requisitionFilters]);

  return { filterConfig, sortOptions };
};
